import React, { useEffect, useState } from 'react';
import { api } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, Mail, RotateCcw, Save, Send, Zap } from 'lucide-react';
import { toast } from 'sonner';

export function ProspectionTemplateCard() {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [variables, setVariables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  // AI suggestion state
  const [aiSubject, setAiSubject] = useState('');
  const [aiBody, setAiBody] = useState('');
  const [aiHook, setAiHook] = useState('');
  const [aiStyle, setAiStyle] = useState('professionnel-chaleureux');
  const [aiLength, setAiLength] = useState('moyen');
  const [aiInstructions, setAiInstructions] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [showAi, setShowAi] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/maps/systeme-u/mail-template');
      setSubject(r.data.subject || '');
      setBody(r.data.body || '');
      setVariables(r.data.variables || []);
    } catch (err) {
      toast.error('Erreur chargement template');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async (useAi = false) => {
    setSaving(true);
    try {
      const payload = useAi
        ? { subject: aiSubject, body: aiBody }
        : { subject, body };
      await api.put('/maps/systeme-u/mail-template', payload);
      if (useAi) {
        setSubject(aiSubject);
        setBody(aiBody);
        setShowAi(false);
        toast.success('Version IA sauvegardée comme template actif');
      } else {
        toast.success('Template sauvegardé');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const loadPreview = async () => {
    try {
      const { data } = await api.post('/maps/systeme-u/preview-mail', { subject, body });
      setPreview(data);
      setShowPreview(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur preview');
    }
  };

  const generateAi = async () => {
    setAiLoading(true);
    try {
      const { data } = await api.post('/maps/systeme-u/generate-ai-template', {
        style: aiStyle,
        longueur: aiLength,
        instructions: aiInstructions,
      });
      setAiSubject(data.subject || '');
      setAiBody(data.body || '');
      setAiHook(data.hook || '');
      setShowAi(true);
      toast.success(`Template IA généré (Claude Sonnet 4.5) · ${data.hook}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur génération IA');
    } finally {
      setAiLoading(false);
    }
  };

  const insertVariable = (v, target = 'body') => {
    const sel = target === 'body'
      ? document.querySelector('textarea[data-testid="template-body-textarea"]')
      : document.querySelector('textarea[data-testid="template-ai-body-textarea"]');
    if (!sel) return;
    const start = sel.selectionStart;
    const end = sel.selectionEnd;
    const current = target === 'body' ? body : aiBody;
    const setter = target === 'body' ? setBody : setAiBody;
    setter(current.slice(0, start) + v + current.slice(end));
    setTimeout(() => {
      sel.focus();
      sel.setSelectionRange(start + v.length, start + v.length);
    }, 0);
  };

  return (
    <Card className="border-2 border-[#047857]/20 shadow-sm bg-gradient-to-br from-[#ECFDF5] to-white" data-testid="prospection-template-card">
      <CardContent className="p-6 space-y-4">
        <div className="flex items-start gap-2.5">
          <div className="w-9 h-9 rounded-lg bg-[#047857]/10 flex items-center justify-center flex-shrink-0">
            <Mail className="w-5 h-5 text-[#047857]" />
          </div>
          <div className="flex-1">
            <h3 className="text-base font-semibold">Template de prospection (Super Admin)</h3>
            <p className="text-xs text-[#71717A] mt-0.5">
              Mail type utilisé pour la colonne "Mail personnalisé" de l'export Excel. Les variables entre accolades sont remplacées automatiquement pour chaque magasin.
            </p>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-20"><Loader2 className="w-5 h-5 animate-spin text-[#047857]"/></div>
        ) : (
          <>
            {/* AI generation controls */}
            <div className="bg-white/80 rounded-lg p-3 border border-[#047857]/20 space-y-2">
              <div className="flex items-center gap-2 text-xs font-semibold text-[#047857] uppercase tracking-wide">
                <Zap className="w-3.5 h-3.5"/> Génération IA (Claude Sonnet 4.5)
              </div>
              <div className="grid grid-cols-2 gap-2">
                <select value={aiStyle} onChange={e => setAiStyle(e.target.value)}
                  className="text-xs rounded border border-[#E5E7EB] px-2 py-1 bg-white"
                  data-testid="ai-style-select">
                  <option value="professionnel-chaleureux">Pro & chaleureux</option>
                  <option value="direct-roi">Direct & ROI</option>
                  <option value="court-percutant">Court & percutant</option>
                  <option value="conversation">Conversationnel</option>
                </select>
                <select value={aiLength} onChange={e => setAiLength(e.target.value)}
                  className="text-xs rounded border border-[#E5E7EB] px-2 py-1 bg-white"
                  data-testid="ai-length-select">
                  <option value="court">Court (&lt;80 mots)</option>
                  <option value="moyen">Moyen (150-200 mots)</option>
                  <option value="long">Long (250-350 mots)</option>
                </select>
              </div>
              <Input placeholder="Instructions optionnelles (ex: mettre l'accent sur la saisonnalité…)"
                value={aiInstructions} onChange={e => setAiInstructions(e.target.value)}
                className="text-xs"
                data-testid="ai-instructions-input"/>
              <Button onClick={generateAi} disabled={aiLoading}
                className="w-full h-8 text-xs bg-[#047857] hover:bg-[#065F46] text-white"
                data-testid="ai-generate-btn">
                {aiLoading ? (
                  <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5"/> Génération IA en cours (10-20s)…</>
                ) : (
                  <><Zap className="w-3.5 h-3.5 mr-1.5"/> Générer une proposition IA</>
                )}
              </Button>
            </div>

            {/* Side-by-side template comparison OR single template view */}
            {showAi ? (
              <div className="grid grid-cols-2 gap-3">
                {/* SAVED TEMPLATE (left) */}
                <div className="space-y-2 p-3 rounded-lg border-2 border-[#64748B]/30 bg-[#F8FAFC]">
                  <div className="flex items-center justify-between">
                    <div className="text-xs font-semibold text-[#334155] uppercase tracking-wide">
                      📝 Sauvegardé (admin)
                    </div>
                    <Button size="sm" onClick={() => save(false)} disabled={saving}
                      className="h-6 text-[10px] bg-[#334155] hover:bg-[#1E293B] text-white"
                      data-testid="template-save-left-btn">
                      Utiliser celui-ci
                    </Button>
                  </div>
                  <Input value={subject} onChange={e => setSubject(e.target.value)} className="text-xs"
                    placeholder="Objet"
                    data-testid="template-subject-input"/>
                  <Textarea value={body} onChange={e => setBody(e.target.value)} rows={14}
                    className="text-xs font-mono leading-relaxed"
                    data-testid="template-body-textarea"/>
                  <div className="text-[10px] text-[#64748B]">{body.length} caractères</div>
                </div>

                {/* AI SUGGESTION (right) */}
                <div className="space-y-2 p-3 rounded-lg border-2 border-[#047857]/40 bg-[#F0FDF4]">
                  <div className="flex items-center justify-between">
                    <div className="text-xs font-semibold text-[#047857] uppercase tracking-wide flex items-center gap-1">
                      ✨ Suggestion IA
                    </div>
                    <Button size="sm" onClick={() => save(true)} disabled={saving}
                      className="h-6 text-[10px] bg-[#047857] hover:bg-[#065F46] text-white"
                      data-testid="template-save-right-btn">
                      Utiliser celui-ci
                    </Button>
                  </div>
                  {aiHook && (
                    <div className="text-[10px] italic text-[#065F46] bg-white/70 px-2 py-1 rounded">
                      💡 {aiHook}
                    </div>
                  )}
                  <Input value={aiSubject} onChange={e => setAiSubject(e.target.value)} className="text-xs"
                    placeholder="Objet"
                    data-testid="template-ai-subject-input"/>
                  <Textarea value={aiBody} onChange={e => setAiBody(e.target.value)} rows={14}
                    className="text-xs font-mono leading-relaxed"
                    data-testid="template-ai-body-textarea"/>
                  <div className="flex items-center justify-between text-[10px] text-[#64748B]">
                    <span>{aiBody.length} caractères</span>
                    <button type="button" onClick={() => setShowAi(false)}
                      className="text-[#64748B] hover:text-[#334155] underline"
                      data-testid="template-hide-ai-btn">
                      Masquer la suggestion
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div>
                  <label className="text-xs font-semibold text-[#334155] mb-1.5 block">Objet du mail</label>
                  <Input value={subject} onChange={e => setSubject(e.target.value)} className="text-sm"
                    data-testid="template-subject-input"/>
                </div>
                <div>
                  <label className="text-xs font-semibold text-[#334155] mb-1.5 block">Corps du mail</label>
                  <Textarea value={body} onChange={e => setBody(e.target.value)} rows={12}
                    className="text-sm font-mono text-[13px] leading-relaxed"
                    data-testid="template-body-textarea"/>
                  <div className="text-[10px] text-[#64748B] mt-1">
                    {body.length} caractères · {body.split(/\s+/).filter(Boolean).length} mots
                  </div>
                </div>
              </>
            )}

            <div>
              <div className="text-xs font-semibold text-[#334155] mb-1.5">Variables disponibles (cliquez pour insérer dans le template actif)</div>
              <div className="flex flex-wrap gap-1.5">
                {variables.map(v => (
                  <button key={v} type="button" onClick={() => insertVariable(v, showAi ? 'ai' : 'body')}
                    className="text-[11px] font-mono px-2 py-0.5 rounded bg-[#D1FAE5] text-[#065F46] hover:bg-[#A7F3D0] border border-[#6EE7B7] transition-colors"
                    data-testid={`template-var-${v.replace(/[{}]/g, '')}`}>
                    {v}
                  </button>
                ))}
              </div>
            </div>

            {!showAi && (
              <div className="flex gap-2">
                <Button onClick={() => save(false)} disabled={saving}
                  className="flex-1 h-9 text-xs bg-[#047857] hover:bg-[#065F46] text-white"
                  data-testid="template-save-btn">
                  {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5"/> : <Save className="w-3.5 h-3.5 mr-1.5"/>}
                  Sauvegarder
                </Button>
                <Button variant="outline" onClick={loadPreview}
                  className="h-9 text-xs border-[#047857]/40 text-[#047857] hover:bg-[#ECFDF5]"
                  data-testid="template-preview-btn">
                  <Send className="w-3.5 h-3.5 mr-1.5"/>
                  Prévisualiser
                </Button>
                <Button variant="outline" onClick={load} disabled={saving}
                  className="h-9 text-xs border-[#64748B]/40 text-[#334155] hover:bg-[#F1F5F9]"
                  data-testid="template-reload-btn">
                  <RotateCcw className="w-3.5 h-3.5 mr-1.5"/>
                  Recharger
                </Button>
              </div>
            )}

            {showPreview && preview && (
              <div className="bg-white border-2 border-[#047857]/30 rounded-lg p-3 space-y-2" data-testid="template-preview-box">
                <div className="text-[11px] text-[#71717A]">
                  Exemple avec : <strong>{preview.store?.enseigne} {preview.store?.ville}</strong>
                  {preview.store?.dirigeants?.[0] && (
                    <> — {preview.store.dirigeants[0].prenom} {preview.store.dirigeants[0].nom}</>
                  )}
                </div>
                <div className="border-l-4 border-[#047857] pl-3 py-1 bg-[#F0FDF4]">
                  <div className="text-xs text-[#71717A] uppercase font-semibold mb-0.5">Objet</div>
                  <div className="text-sm font-medium">{preview.subject}</div>
                </div>
                <div className="border-l-4 border-[#047857] pl-3 py-1 bg-[#F0FDF4]">
                  <div className="text-xs text-[#71717A] uppercase font-semibold mb-0.5">Corps</div>
                  <pre className="text-[13px] whitespace-pre-wrap font-sans leading-relaxed">{preview.body}</pre>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setShowPreview(false)}
                  className="h-7 text-xs text-[#64748B]"
                  data-testid="template-preview-close">
                  Fermer
                </Button>
              </div>
            )}

            <div className="bg-[#ECFDF5] border border-[#6EE7B7] rounded-lg p-2.5 text-[11px] text-[#065F46]">
              💡 <strong>Workflow</strong> : éditez votre template ici → "Sauvegarder" → dans la carte Trombinoscope Système U, cliquez "Exporter Excel + mails personnalisés" pour télécharger le fichier avec une colonne <em>Mail - Objet</em> et <em>Mail - Corps</em> rendus pour chacun des 741 prospects (avec prénom, nom du dirigeant, enseigne, ville…).
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
